import type { EmissaoFaturamento } from '../../../../src/lib/financeiro/metricasFaturamento.ts';

/** Só o que o dashboard lê de `faturas`. */
export interface FaturaBrutaEmissao {
  id: string;
  uc_codigo?: string | null;
  cliente_nome?: string | null;
  mes_referencia?: string | null;
  data_vencimento?: string | null;
  classificacao?: string | null;
  created_at?: string | null;
  emitido_em?: string | null;
  emitido_por_nome?: string | null;
  historico_emissoes?: {
    emitido_por_nome?: string | null;
    emitido_em?: string | null;
    registrado_em?: string | null;
    motivo_atraso_emissao?: string | null;
    justificativa_atraso_emissao?: string | null;
  }[] | null;
}

/**
 * Achata uma fatura nas emissões que ela sofreu.
 *
 * `historico_emissoes` é a fonte: `emitido_por_nome`/`emitido_em` da fatura são
 * sobrescritos a cada reemissão (decisão de 19/08/2026) e creditariam ao último
 * emissor o trabalho do primeiro — foi o caso da fatura 9486, em que o Ítalo
 * emitiu em 03/08 e a tela passou a creditar tudo ao João depois da reemissão.
 *
 * Faturas emitidas antes do gatilho de 03/09/2026 têm histórico vazio. Para
 * essas, a emissão viva é a única que existe e entra como ordem 0 — senão o
 * dashboard nasceria cego para todo o passado.
 */
export function achatarEmissoes(f: FaturaBrutaEmissao): EmissaoFaturamento[] {
  const base = {
    faturaId: f.id,
    ucCodigo: f.uc_codigo ?? null,
    clienteNome: f.cliente_nome ?? null,
    mesReferencia: f.mes_referencia ?? null,
    // A etapa "Fatura disponível" é representada por `status_fatura IS NULL` e não
    // tem carimbo próprio; a criação da linha é o instante mais próximo que existe.
    disponivelEm: f.created_at ?? null,
    dataVencimento: f.data_vencimento ?? null,
    classificacao: f.classificacao ?? null,
  };

  const historico = f.historico_emissoes ?? [];
  if (historico.length > 0) {
    return historico
      .map((h, i) => ({
        ...base,
        emitidoPorNome: h.emitido_por_nome || 'Sem atribuição',
        // `registrado_em` é now() do servidor; `emitido_em` é o relógio do
        // navegador de quem clicou, que já se provou divergente em minutos
        // entre máquinas (João +2min, Dhaiane +3min em 26/08/2026).
        emitidoEm: h.registrado_em || h.emitido_em || '',
        ordem: i,
        motivo: h.motivo_atraso_emissao ?? null,
        justificativa: h.justificativa_atraso_emissao ?? null,
      }))
      .filter((e) => !!e.emitidoEm);
  }

  if (!f.emitido_em) return [];
  return [{
    ...base,
    emitidoPorNome: f.emitido_por_nome || 'Sem atribuição',
    emitidoEm: f.emitido_em,
    ordem: 0,
    motivo: null,
    justificativa: null,
  }];
}
